<script lang="ts">
  import {
    Veterinaria,
    type Propietario, type Mascota, type Vacuna, type PesoRegistro,
    type Jaula, type InternamientoVet, type ProductoVet, type VentaVetItem,
  } from '../lib/api';

  type Sub = 'mascotas' | 'internamiento' | 'mostrador';

  let {
    initialSub = 'mascotas' as Sub,
    showSubNav = true,
    catalogFilter = null as 'ALIMENTO' | 'MEDICAMENTO' | null,
    title = 'Veterinaria',
  }: {
    initialSub?: Sub;
    showSubNav?: boolean;
    catalogFilter?: 'ALIMENTO' | 'MEDICAMENTO' | null;
    title?: string;
  } = $props();

  let sub = $state<Sub>('mascotas');
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  function fail(e: any) { error = e?.message ?? String(e); info = null; }
  function ok(m: string) { info = m; error = null; }

  // ── Mascotas / dueños ──────────────────────────────────────────────────────
  let propietarios = $state<Propietario[]>([]);
  let mascotas = $state<Mascota[]>([]);
  let seleccion = $state<Mascota | null>(null);
  let vacunas = $state<Vacuna[]>([]);
  let pesos = $state<PesoRegistro[]>([]);

  let nuevoDueno = $state<Propietario>({ nombre: '', telefono: '' });
  let nuevaMascota = $state<Mascota>({ propietarioId: '', nombre: '', especie: 'PERRO', sexo: 'DESCONOCIDO' });
  let nuevaVacuna = $state<Vacuna>({ nombre: '', fechaAplicacion: hoy(), proximaDosis: null, lote: '', veterinario: '' });
  let nuevoPeso = $state<PesoRegistro>({ fecha: hoy(), pesoKg: 0, nota: '' });

  function hoy() { return new Date().toISOString().slice(0, 10); }

  async function cargarMascotas() {
    try {
      [propietarios, mascotas] = await Promise.all([Veterinaria.propietarios(), Veterinaria.mascotas()]);
    } catch (e) { fail(e); }
  }

  async function crearDueno(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevoDueno.nombre.trim()) { error = 'Nombre del dueño requerido.'; return; }
    try {
      await Veterinaria.crearPropietario(nuevoDueno);
      nuevoDueno = { nombre: '', telefono: '' };
      ok('Dueño registrado.');
      await cargarMascotas();
    } catch (e) { fail(e); }
  }

  async function crearMascota(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevaMascota.propietarioId) { error = 'Selecciona el dueño.'; return; }
    if (!nuevaMascota.nombre.trim()) { error = 'Nombre de la mascota requerido.'; return; }
    try {
      await Veterinaria.crearMascota(nuevaMascota);
      nuevaMascota = { propietarioId: '', nombre: '', especie: 'PERRO', sexo: 'DESCONOCIDO' };
      ok('Mascota registrada.');
      await cargarMascotas();
    } catch (e) { fail(e); }
  }

  async function abrirFicha(m: Mascota) {
    seleccion = m;
    try {
      [vacunas, pesos] = await Promise.all([Veterinaria.vacunas(m.id!), Veterinaria.pesos(m.id!)]);
    } catch (e) { fail(e); }
  }

  async function agregarVacuna(e: SubmitEvent) {
    e.preventDefault();
    if (!seleccion?.id) return;
    try {
      await Veterinaria.agregarVacuna(seleccion.id, nuevaVacuna);
      nuevaVacuna = { nombre: '', fechaAplicacion: hoy(), proximaDosis: null, lote: '', veterinario: '' };
      ok('Vacuna registrada en el carné.');
      vacunas = await Veterinaria.vacunas(seleccion.id);
    } catch (e) { fail(e); }
  }

  async function registrarPeso(e: SubmitEvent) {
    e.preventDefault();
    if (!seleccion?.id) return;
    if (!nuevoPeso.pesoKg) { error = 'Peso requerido.'; return; }
    try {
      await Veterinaria.registrarPeso(seleccion.id, nuevoPeso);
      nuevoPeso = { fecha: hoy(), pesoKg: 0, nota: '' };
      ok('Peso registrado.');
      [pesos, mascotas] = await Promise.all([Veterinaria.pesos(seleccion.id), Veterinaria.mascotas()]);
      seleccion = mascotas.find(x => x.id === seleccion?.id) ?? seleccion;
    } catch (e) { fail(e); }
  }

  function nombreDueno(id?: string | null) {
    return propietarios.find(p => p.id === id)?.nombre ?? '—';
  }

  // ── Internamiento ──────────────────────────────────────────────────────────
  let jaulas = $state<Jaula[]>([]);
  let activos = $state<InternamientoVet[]>([]);
  let nuevaJaula = $state<Jaula>({ numero: '', area: '', tamano: 'MEDIANA', estado: 'DISPONIBLE' });
  let nuevoInt = $state<InternamientoVet>({ mascotaId: '', jaulaId: '', motivo: '', veterinario: '' });

  async function cargarInternamiento() {
    try {
      [jaulas, activos] = await Promise.all([Veterinaria.jaulas(), Veterinaria.internamientosActivos()]);
      if (mascotas.length === 0) mascotas = await Veterinaria.mascotas();
    } catch (e) { fail(e); }
  }

  async function crearJaula(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevaJaula.numero.trim()) { error = 'Número de jaula requerido.'; return; }
    try {
      await Veterinaria.crearJaula(nuevaJaula);
      nuevaJaula = { numero: '', area: '', tamano: 'MEDIANA', estado: 'DISPONIBLE' };
      ok('Jaula creada.');
      jaulas = await Veterinaria.jaulas();
    } catch (e) { fail(e); }
  }

  async function internar(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevoInt.mascotaId || !nuevoInt.jaulaId) { error = 'Selecciona mascota y jaula.'; return; }
    try {
      await Veterinaria.internar(nuevoInt);
      nuevoInt = { mascotaId: '', jaulaId: '', motivo: '', veterinario: '' };
      ok('Mascota internada.');
      await cargarInternamiento();
    } catch (e) { fail(e); }
  }

  async function darAlta(id: string) {
    try { await Veterinaria.altaInternamiento(id); ok('Alta registrada.'); await cargarInternamiento(); }
    catch (e) { fail(e); }
  }

  function nombreMascota(id?: string | null) {
    return mascotas.find(m => m.id === id)?.nombre ?? id ?? '—';
  }
  function numeroJaula(id?: string | null) {
    return jaulas.find(j => j.id === id)?.numero ?? '—';
  }

  // ── Mostrador / farmacia ────────────────────────────────────────────────────
  let productos = $state<ProductoVet[]>([]);
  let nuevoProd = $state<ProductoVet>({ codigo: '', nombre: '', categoria: 'ALIMENTO', precio: 0, stock: 0, unidad: '', soloPacientes: false });
  let carrito = $state<Array<VentaVetItem & { nombre: string }>>([]);
  let ventaPropietario = $state('');
  let ventaMascota = $state('');
  let ventaFormaPago = $state<'EFECTIVO' | 'TARJETA' | 'CUENTA' | 'TRANSFERENCIA'>('EFECTIVO');

  const totalCarrito = $derived(
    carrito.reduce((s, it) => s + (it.precioUnitario ?? 0) * (it.cantidad ?? 0), 0)
  );
  const requierePaciente = $derived(
    carrito.some(it => productos.find(p => p.id === it.productoId)?.soloPacientes)
  );
  const productosFiltrados = $derived(
    catalogFilter ? productos.filter(p => p.categoria === catalogFilter) : productos
  );

  $effect(() => { sub = initialSub; });

  async function cargarMostrador() {
    try {
      productos = await Veterinaria.productos();
      if (propietarios.length === 0) propietarios = await Veterinaria.propietarios();
      if (mascotas.length === 0) mascotas = await Veterinaria.mascotas();
    } catch (e) { fail(e); }
  }

  async function crearProducto(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevoProd.codigo.trim() || !nuevoProd.nombre.trim()) { error = 'Código y nombre requeridos.'; return; }
    try {
      await Veterinaria.crearProducto(nuevoProd);
      nuevoProd = { codigo: '', nombre: '', categoria: 'ALIMENTO', precio: 0, stock: 0, unidad: '', soloPacientes: false };
      ok('Producto agregado al catálogo.');
      productos = await Veterinaria.productos();
    } catch (e) { fail(e); }
  }

  function agregarAlCarrito(p: ProductoVet) {
    const existe = carrito.find(it => it.productoId === p.id);
    if (existe) { existe.cantidad += 1; carrito = [...carrito]; }
    else carrito = [...carrito, { productoId: p.id, nombre: p.nombre, cantidad: 1, precioUnitario: p.precio }];
  }
  function quitarDelCarrito(i: number) { carrito = carrito.filter((_, idx) => idx !== i); }

  async function cobrar() {
    if (carrito.length === 0) { error = 'El carrito está vacío.'; return; }
    if (requierePaciente && !ventaMascota) { error = 'Hay productos de farmacia (solo pacientes): selecciona la mascota.'; return; }
    try {
      const r = await Veterinaria.crearVenta({
        propietarioId: ventaPropietario || undefined,
        mascotaId: ventaMascota || undefined,
        formaPago: ventaFormaPago,
        items: carrito.map(it => ({ productoId: it.productoId, cantidad: it.cantidad, precioUnitario: it.precioUnitario })),
      });
      ok(`Venta registrada. Total Q ${Number(r.venta.total ?? 0).toFixed(2)}.`);
      carrito = []; ventaPropietario = ''; ventaMascota = '';
      productos = await Veterinaria.productos();
    } catch (e) { fail(e); }
  }

  // Carga perezosa al cambiar de sub-pestaña.
  $effect(() => {
    if (sub === 'mascotas') cargarMascotas();
    else if (sub === 'internamiento') cargarInternamiento();
    else if (sub === 'mostrador') cargarMostrador();
  });
</script>

<div class="vet">
  <header class="vhead">
    <h2>{title}</h2>
    {#if showSubNav}
      <nav class="subtabs">
        <button class:active={sub==='mascotas'} onclick={() => sub='mascotas'}>Mascotas y dueños</button>
        <button class:active={sub==='internamiento'} onclick={() => sub='internamiento'}>Internamiento</button>
        <button class:active={sub==='mostrador'} onclick={() => sub='mostrador'}>Mostrador / Farmacia</button>
      </nav>
    {/if}
  </header>

  {#if error}<p class="msg err">{error}</p>{/if}
  {#if info}<p class="msg ok">{info}</p>{/if}

  {#if sub === 'mascotas'}
    <div class="grid2">
      <section class="card">
        <h3>Nuevo dueño</h3>
        <form onsubmit={crearDueno} class="form">
          <input placeholder="Nombre completo" bind:value={nuevoDueno.nombre} />
          <input placeholder="Teléfono" bind:value={nuevoDueno.telefono} />
          <input placeholder="Email" bind:value={nuevoDueno.email} />
          <input placeholder="Documento (DPI/NIT)" bind:value={nuevoDueno.documento} />
          <button type="submit">Registrar dueño</button>
        </form>
      </section>

      <section class="card">
        <h3>Nueva mascota</h3>
        <form onsubmit={crearMascota} class="form">
          <select bind:value={nuevaMascota.propietarioId}>
            <option value="">— Dueño —</option>
            {#each propietarios as p (p.id)}<option value={p.id}>{p.nombre}</option>{/each}
          </select>
          <input placeholder="Nombre" bind:value={nuevaMascota.nombre} />
          <select bind:value={nuevaMascota.especie}>
            {#each ['PERRO','GATO','AVE','CONEJO','ROEDOR','REPTIL','EQUINO','OTRO'] as e}<option>{e}</option>{/each}
          </select>
          <input placeholder="Raza" bind:value={nuevaMascota.raza} />
          <select bind:value={nuevaMascota.sexo}>
            {#each ['MACHO','HEMBRA','DESCONOCIDO'] as s}<option>{s}</option>{/each}
          </select>
          <input type="date" bind:value={nuevaMascota.fechaNacimiento} />
          <button type="submit">Registrar mascota</button>
        </form>
      </section>
    </div>

    <section class="card">
      <h3>Mascotas ({mascotas.length})</h3>
      <table>
        <thead><tr><th>Nombre</th><th>Especie</th><th>Raza</th><th>Sexo</th><th>Dueño</th><th>Peso (kg)</th><th></th></tr></thead>
        <tbody>
          {#each mascotas as m (m.id)}
            <tr class:sel={seleccion?.id===m.id}>
              <td>{m.nombre}</td><td>{m.especie}</td><td>{m.raza ?? '—'}</td><td>{m.sexo}</td>
              <td>{nombreDueno(m.propietarioId)}</td><td>{m.pesoActual ?? '—'}</td>
              <td><button class="link" onclick={() => abrirFicha(m)}>Ficha</button></td>
            </tr>
          {/each}
          {#if mascotas.length === 0}<tr><td colspan="7" class="muted">Sin mascotas registradas.</td></tr>{/if}
        </tbody>
      </table>
    </section>

    {#if seleccion}
      <div class="grid2">
        <section class="card">
          <h3>Carné de vacunas — {seleccion.nombre}</h3>
          <form onsubmit={agregarVacuna} class="form inline">
            <input placeholder="Vacuna (Rabia…)" bind:value={nuevaVacuna.nombre} />
            <input type="date" bind:value={nuevaVacuna.fechaAplicacion} />
            <input type="date" placeholder="Próxima" bind:value={nuevaVacuna.proximaDosis} />
            <button type="submit">+ Vacuna</button>
          </form>
          <table>
            <thead><tr><th>Vacuna</th><th>Aplicada</th><th>Próxima</th></tr></thead>
            <tbody>
              {#each vacunas as v (v.id)}<tr><td>{v.nombre}</td><td>{v.fechaAplicacion}</td><td>{v.proximaDosis ?? '—'}</td></tr>{/each}
              {#if vacunas.length === 0}<tr><td colspan="3" class="muted">Sin vacunas.</td></tr>{/if}
            </tbody>
          </table>
        </section>

        <section class="card">
          <h3>Peso histórico — {seleccion.nombre}</h3>
          <form onsubmit={registrarPeso} class="form inline">
            <input type="date" bind:value={nuevoPeso.fecha} />
            <input type="number" step="0.01" placeholder="kg" bind:value={nuevoPeso.pesoKg} />
            <input placeholder="Nota" bind:value={nuevoPeso.nota} />
            <button type="submit">+ Peso</button>
          </form>
          <table>
            <thead><tr><th>Fecha</th><th>Peso (kg)</th><th>Nota</th></tr></thead>
            <tbody>
              {#each pesos as p (p.id)}<tr><td>{p.fecha}</td><td>{p.pesoKg}</td><td>{p.nota ?? '—'}</td></tr>{/each}
              {#if pesos.length === 0}<tr><td colspan="3" class="muted">Sin registros.</td></tr>{/if}
            </tbody>
          </table>
        </section>
      </div>
    {/if}

  {:else if sub === 'internamiento'}
    <div class="grid2">
      <section class="card">
        <h3>Nueva jaula</h3>
        <form onsubmit={crearJaula} class="form inline">
          <input placeholder="Número" bind:value={nuevaJaula.numero} />
          <input placeholder="Área" bind:value={nuevaJaula.area} />
          <select bind:value={nuevaJaula.tamano}>
            {#each ['PEQUENA','MEDIANA','GRANDE','JAULON'] as t}<option>{t}</option>{/each}
          </select>
          <button type="submit">+ Jaula</button>
        </form>
        <table>
          <thead><tr><th>Número</th><th>Área</th><th>Tamaño</th><th>Estado</th></tr></thead>
          <tbody>
            {#each jaulas as j (j.id)}
              <tr><td>{j.numero}</td><td>{j.area ?? '—'}</td><td>{j.tamano}</td>
                <td><span class="badge" class:occ={j.estado!=='DISPONIBLE'}>{j.estado}</span></td></tr>
            {/each}
            {#if jaulas.length === 0}<tr><td colspan="4" class="muted">Sin jaulas.</td></tr>{/if}
          </tbody>
        </table>
      </section>

      <section class="card">
        <h3>Internar mascota</h3>
        <form onsubmit={internar} class="form">
          <select bind:value={nuevoInt.mascotaId}>
            <option value="">— Mascota —</option>
            {#each mascotas as m (m.id)}<option value={m.id}>{m.nombre}</option>{/each}
          </select>
          <select bind:value={nuevoInt.jaulaId}>
            <option value="">— Jaula disponible —</option>
            {#each jaulas.filter(j => j.estado==='DISPONIBLE') as j (j.id)}<option value={j.id}>{j.numero} ({j.tamano})</option>{/each}
          </select>
          <input placeholder="Motivo" bind:value={nuevoInt.motivo} />
          <input placeholder="Veterinario" bind:value={nuevoInt.veterinario} />
          <button type="submit">Internar</button>
        </form>
      </section>
    </div>

    <section class="card">
      <h3>Internamientos activos ({activos.length})</h3>
      <table>
        <thead><tr><th>Mascota</th><th>Jaula</th><th>Motivo</th><th>Veterinario</th><th>Ingreso</th><th></th></tr></thead>
        <tbody>
          {#each activos as it (it.id)}
            <tr>
              <td>{nombreMascota(it.mascotaId)}</td><td>{numeroJaula(it.jaulaId)}</td>
              <td>{it.motivo ?? '—'}</td><td>{it.veterinario ?? '—'}</td>
              <td>{it.ingresadoEn?.slice(0,16).replace('T',' ') ?? '—'}</td>
              <td><button class="link" onclick={() => darAlta(it.id!)}>Dar alta</button></td>
            </tr>
          {/each}
          {#if activos.length === 0}<tr><td colspan="6" class="muted">Sin internamientos activos.</td></tr>{/if}
        </tbody>
      </table>
    </section>

  {:else}
    <div class="grid2">
      <section class="card">
        <h3>Nuevo producto / servicio</h3>
        <form onsubmit={crearProducto} class="form">
          <input placeholder="Código" bind:value={nuevoProd.codigo} />
          <input placeholder="Nombre" bind:value={nuevoProd.nombre} />
          <select bind:value={nuevoProd.categoria}>
            {#each ['ALIMENTO','MEDICAMENTO','ACCESORIO','SERVICIO','OTRO'] as c}<option>{c}</option>{/each}
          </select>
          <input type="number" step="0.01" placeholder="Precio Q" bind:value={nuevoProd.precio} />
          <input type="number" placeholder="Stock" bind:value={nuevoProd.stock} />
          <input placeholder="Unidad (bolsa, tableta…)" bind:value={nuevoProd.unidad} />
          <label class="chk"><input type="checkbox" bind:checked={nuevoProd.soloPacientes} /> Solo pacientes (farmacia)</label>
          <button type="submit">Agregar al catálogo</button>
        </form>
      </section>

      <section class="card">
        <h3>Catálogo</h3>
        <table>
          <thead><tr><th>Producto</th><th>Categoría</th><th>Precio</th><th>Stock</th><th></th></tr></thead>
          <tbody>
            {#each productosFiltrados as p (p.id)}
              <tr>
                <td>{p.nombre}{#if p.soloPacientes}<span class="tag">Rx</span>{/if}</td>
                <td>{p.categoria}</td><td>Q {Number(p.precio).toFixed(2)}</td><td>{p.stock}</td>
                <td><button class="link" disabled={p.stock<=0} onclick={() => agregarAlCarrito(p)}>Agregar</button></td>
              </tr>
            {/each}
            {#if productos.length === 0}<tr><td colspan="5" class="muted">Sin productos.</td></tr>{/if}
          </tbody>
        </table>
      </section>
    </div>

    <section class="card">
      <h3>Venta de mostrador</h3>
      <div class="form inline">
        <select bind:value={ventaPropietario}>
          <option value="">— Dueño (opcional) —</option>
          {#each propietarios as p (p.id)}<option value={p.id}>{p.nombre}</option>{/each}
        </select>
        <select bind:value={ventaMascota}>
          <option value="">— Mascota {requierePaciente ? '(requerida)' : '(opcional)'} —</option>
          {#each mascotas as m (m.id)}<option value={m.id}>{m.nombre}</option>{/each}
        </select>
        <select bind:value={ventaFormaPago}>
          {#each ['EFECTIVO','TARJETA','CUENTA','TRANSFERENCIA'] as f}<option>{f}</option>{/each}
        </select>
      </div>
      <table>
        <thead><tr><th>Producto</th><th>Cantidad</th><th>Precio</th><th>Subtotal</th><th></th></tr></thead>
        <tbody>
          {#each carrito as it, i (i)}
            <tr>
              <td>{it.nombre}</td>
              <td><input class="qty" type="number" min="1" bind:value={it.cantidad} /></td>
              <td>Q {Number(it.precioUnitario).toFixed(2)}</td>
              <td>Q {((it.precioUnitario ?? 0) * (it.cantidad ?? 0)).toFixed(2)}</td>
              <td><button class="link" onclick={() => quitarDelCarrito(i)}>Quitar</button></td>
            </tr>
          {/each}
          {#if carrito.length === 0}<tr><td colspan="5" class="muted">Carrito vacío.</td></tr>{/if}
        </tbody>
      </table>
      <div class="total-row">
        <strong>Total: Q {totalCarrito.toFixed(2)}</strong>
        <button class="primary" disabled={carrito.length===0} onclick={cobrar}>Cobrar</button>
      </div>
    </section>
  {/if}
</div>

<style>
  .vet { display: flex; flex-direction: column; gap: 1rem; }
  .vhead { display: flex; align-items: baseline; gap: 1rem; flex-wrap: wrap; }
  .vhead h2 { margin: 0; color: #166534; font-size: 1.4rem; }
  .subtabs { display: flex; gap: .25rem; flex-wrap: wrap; }
  .subtabs button { padding: .4rem .9rem; border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; cursor: pointer; color: #15803d; font: inherit; }
  .subtabs button.active { background: #16a34a; color: #fff; border-color: #16a34a; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  @media (max-width: 820px) { .grid2 { grid-template-columns: 1fr; } }
  .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 1rem; background: #fff; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .card h3 { margin: 0 0 .6rem; color: #166534; font-size: 1rem; }
  .form { display: flex; flex-direction: column; gap: .5rem; }
  .form.inline { flex-direction: row; flex-wrap: wrap; align-items: center; }
  input, select { padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; font: inherit; background: #fff; color: #111827; }
  input:focus, select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .chk { display: flex; align-items: center; gap: .4rem; font-size: .85rem; color: #6b7280; }
  button { font: inherit; }
  .form button[type=submit], .primary { padding: .5rem .9rem; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0; border-radius: 8px; cursor: pointer; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  .form button[type=submit]:hover, .primary:hover { transform: translateY(-1px); }
  .primary:disabled, .link:disabled { opacity: .5; cursor: not-allowed; }
  .link { background: none; border: 0; color: #16a34a; cursor: pointer; text-decoration: underline; padding: 0; }
  table { width: 100%; border-collapse: collapse; margin-top: .5rem; font-size: .88rem; }
  th { text-align: left; padding: .5rem .8rem; color: #6b7280; font-weight: 600; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; background: #f0fdf4; }
  td { text-align: left; padding: .4rem .8rem; border-top: 1px solid #f3f4f6; color: #111827; }
  tr.sel { background: #f0fdf4; }
  .muted { color: #6b7280; text-align: center; padding: .8rem; }
  .badge { padding: .1rem .5rem; border-radius: 999px; background: #dcfce7; color: #166534; font-size: .72rem; }
  .badge.occ { background: #fee2e2; color: #dc2626; }
  .tag { margin-left: .4rem; padding: .05rem .35rem; border-radius: 4px; background: #fee2e2; color: #dc2626; font-size: .65rem; font-weight: 700; }
  .qty { width: 64px; }
  .total-row { display: flex; justify-content: flex-end; align-items: center; gap: 1rem; margin-top: .75rem; }
  .msg { padding: .55rem .8rem; border-radius: 8px; margin: 0; }
  .msg.err { background: #fee2e2; color: #dc2626; }
  .msg.ok { background: #dcfce7; color: #166534; }
</style>
